import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Select, Spin } from 'antd';
import axiosInstance from '../../../../config/axiosConfig';
import './PricingCalculator.css';

const { Option } = Select;

// Convert ISO country code to flag emoji (only valid 2-letter codes)
const codeToFlag = (code) => {
  if (!code || code.length !== 2 || !/^[A-Z]{2}$/i.test(code)) return '';
  try {
    const flag = String.fromCodePoint(
      ...[...code.toUpperCase()].map((c) => 127397 + c.charCodeAt(0))
    );
    // Test if the flag actually renders (some 2-letter combos are not real countries)
    return flag;
  } catch {
    return '';
  }
};

// Get currency symbol via Intl API
const getCurrencySymbol = (currency) => {
  try {
    const parts = new Intl.NumberFormat('en', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    const sym = parts.find((p) => p.type === 'currency');
    return sym ? sym.value : currency;
  } catch {
    return currency;
  }
};

// Format price with proper decimals
const formatPrice = (value, currency) => {
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(value);
  } catch {
    return `${getCurrencySymbol(currency)} ${Number(value).toFixed(4)}`;
  }
};

// Get currency display name from code
const getCurrencyName = (code) => {
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'currency' }).of(code);
    return name || code;
  } catch {
    return code;
  }
};

const CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];

const PricingCalculator = ({ visible, onClose }) => {
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [selectedCategory, setSelectedCategory] = useState('MARKETING');
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  const [error, setError] = useState(null);

  // Track the latest request to avoid stale responses
  const requestRef = useRef(0);

  // ── Fetch countries & currencies on first open ──
  const initialized = useRef(false);

  useEffect(() => {
    if (!visible || initialized.current) return;
    initialized.current = true;

    const load = async () => {
      setInitLoading(true);
      try {
        const [countriesRes, currenciesRes] = await Promise.all([
          axiosInstance.get('/v1/pricing/countries'),
          axiosInstance.get('/v1/pricing/currencies'),
        ]);
        setCountries(countriesRes.data?.entity || []);
        setCurrencies(currenciesRes.data?.entity || []);
      } catch (err) {
        console.error('Failed to load pricing metadata:', err);
      } finally {
        setInitLoading(false);
      }
    };
    load();
  }, [visible]);

  // ── Fetch pricing whenever selection changes ──
  const fetchPricing = useCallback(async (countryCode, category, currency) => {
    if (!countryCode) return;
    const id = ++requestRef.current;

    setLoading(true);
    setError(null);

    try {
      const params = { countryCode, category };
      if (currency && currency !== 'USD') params.currency = currency;

      const { data } = await axiosInstance.get('/v1/pricing', { params });

      // Discard stale response
      if (id !== requestRef.current) return;

      if (!data.status || data.statuscode !== 200) {
        throw new Error(data.error || data.message || 'Failed to fetch pricing');
      }
      setPricing(data.entity);
      setError(null);
    } catch (err) {
      if (id !== requestRef.current) return;
      setPricing(null);
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Failed to fetch pricing');
    } finally {
      if (id === requestRef.current) setLoading(false);
    }
  }, []);

  // Trigger price fetch on selection change
  useEffect(() => {
    if (selectedCountry) {
      fetchPricing(selectedCountry.code, selectedCategory, selectedCurrency);
    }
  }, [selectedCountry, selectedCategory, selectedCurrency, fetchPricing]);

  // ── Handlers ──
  const handleCountryChange = (code) => {
    const country = countries.find((c) => c.code === code) || null;
    setSelectedCountry(country);
  };

  const handleCurrencyChange = (val) => setSelectedCurrency(val);
  const handleCategoryChange = (cat) => setSelectedCategory(cat);

  // ── Render: Price display area ──
  const renderPriceDisplay = () => {
    if (!selectedCountry) {
      return (
        <div className="wa-pricing-empty">
          <div className="wa-pricing-empty-icon">🌍</div>
          <p>Select a market to see pricing</p>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="wa-pricing-skeleton">
          <div className="wa-pricing-skeleton-line" />
          <div className="wa-pricing-skeleton-small" />
          <div className="wa-pricing-skeleton-small" />
        </div>
      );
    }

    if (error) {
      return <div className="wa-pricing-error">{error}</div>;
    }

    if (!pricing) return null;

    const displayCurrency = pricing.currency || selectedCurrency;
    const isConverted = pricing.priceSource === 'converted';

    return (
      <div className="wa-pricing-display">
        <div className="wa-pricing-price">
          {formatPrice(pricing.price, displayCurrency)}
        </div>
        <div className="wa-pricing-per">per message</div>
        {isConverted && (
          <div className="wa-pricing-converted-note">
            Converted from USD, may vary slightly
          </div>
        )}
        <div className="wa-pricing-divider" />
        <div className="wa-pricing-meta">
          {pricing.exchangeRate && isConverted && (
            <span>
              Exchange rate: 1 USD = {Number(pricing.exchangeRate).toFixed(2)} {displayCurrency}
            </span>
          )}
          {pricing.availableCurrencies && pricing.availableCurrencies.length > 0 && (
            <span>
              Exact pricing in: {pricing.availableCurrencies.join(', ')}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={560}
      centered
      styles={{ body: { padding: 0 } }}
    >
      <div className="wa-pricing-wrapper">
        <div className="wa-pricing-card">

          {/* Header */}
          <div className="wa-pricing-header">
            <h3>
              <span role="img" aria-label="whatsapp">💬</span>
              WhatsApp Business Pricing
            </h3>
            <p>Estimate the cost of your messages</p>
          </div>

          {initLoading ? (
            <div style={{ textAlign: 'center', padding: '3rem 0' }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              {/* Market selector */}
              <div className="wa-pricing-field">
                <label className="wa-pricing-label">Market</label>
                <Select
                  showSearch
                  placeholder="Select a market"
                  value={selectedCountry?.code || undefined}
                  onChange={handleCountryChange}
                  filterOption={(input, option) =>
                    (option?.searchtext || '').toLowerCase().includes(input.toLowerCase())
                  }
                  optionLabelProp="label"
                  virtual={false}
                >
                  {countries.map((c) => (
                    <Option
                      key={c.code}
                      value={c.code}
                      label={`${codeToFlag(c.code) ? codeToFlag(c.code) + ' ' : ''}${c.name}`}
                      searchtext={`${c.name} ${c.code} ${c.phoneCode}`}
                    >
                      <div className="wa-pricing-country-option">
                        {codeToFlag(c.code) && (
                          <span className="wa-pricing-country-flag">{codeToFlag(c.code)}</span>
                        )}
                        <span className="wa-pricing-country-name">{c.name}</span>
                        <span className="wa-pricing-country-phone">{c.phoneCode}</span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>

              {/* Currency selector */}
              <div className="wa-pricing-field">
                <label className="wa-pricing-label">Currency</label>
                <Select
                  showSearch
                  value={selectedCurrency}
                  onChange={handleCurrencyChange}
                  filterOption={(input, option) =>
                    (option?.searchtext || '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {currencies.map((cur) => (
                    <Option key={cur} value={cur} searchtext={`${getCurrencyName(cur)} ${cur}`}>
                      {getCurrencyName(cur)} ({cur})
                    </Option>
                  ))}
                </Select>
              </div>

              {/* Category pills */}
              <div className="wa-pricing-field">
                <label className="wa-pricing-label">Category</label>
                <div className="wa-pricing-pills">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      className={`wa-pricing-pill${selectedCategory === cat ? ' active' : ''}`}
                      onClick={() => handleCategoryChange(cat)}
                    >
                      {cat.charAt(0) + cat.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="wa-pricing-divider" />

              {/* Price display */}
              {renderPriceDisplay()}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PricingCalculator;
